import { useState, useEffect, useReducer, useCallback, useRef } from "react";

const API = process.env.REACT_APP_API_URL || "";
const GREEN  = "#22c55e";
const RED    = "#ef4444";
const YELLOW = "#f59e0b";
const TEAL   = "#2E7D8C";
const SAGE   = "#5C8A5A";

// ── THEME SYSTEM (light / dark / mono) — unified executive look ────
const THEMES = {
  light: { bg:"#f4f7f4", surface:"#ffffff", border:"#d4e4d3", text:"#1a2e1a", textSub:"#4a6b49", textMuted:"#7a9e79", accent:SAGE,      headerBg:"#ffffff", inputBg:"#f4f7f4", accentLt:"#eef3ee" },
  dark:  { bg:"#161b27", surface:"#1e2638", border:"#2d3a50", text:"#f1f5f9", textSub:"#94a3b8", textMuted:"#7e8ca3", accent:"#6FAE6C", headerBg:"#111827", inputBg:"#0f1623", accentLt:"#222e22" },
  mono:  { bg:"#ffffff", surface:"#fafafa", border:"#e2e2e2", text:"#111111", textSub:"#555555", textMuted:"#8a8a8a", accent:"#1a1a1a", headerBg:"#ffffff", inputBg:"#f7f7f7", accentLt:"#ededed" },
};
Object.values(THEMES).forEach(t => { t.accentGrad = t.accent; });
const THEME_CYCLE = ["mono", "light", "dark"];
let _themeName = "mono";
try { const s = localStorage.getItem("cp_theme"); if (s && THEMES[s]) _themeName = s; } catch (e) {}
let _T = THEMES[_themeName];
const _themeSubs = new Set();
function setThemeName(name) {
  if (!THEMES[name]) return;
  _themeName = name; _T = THEMES[name];
  try { localStorage.setItem("cp_theme", name); } catch (e) {}
  _themeSubs.forEach(fn => fn());
}
// T is a live proxy onto the active theme: every existing T.x reference
// re-reads the current theme, so switching needs no per-component edits.
const T = new Proxy({}, {
  get: (_, k) => _T[k],
  has: (_, k) => k in _T,
  ownKeys: () => Reflect.ownKeys(_T),
  getOwnPropertyDescriptor: (_, k) => Object.getOwnPropertyDescriptor(_T, k),
});
function useThemeName() {
  const [, force] = useReducer(x => x + 1, 0);
  useEffect(() => { _themeSubs.add(force); return () => { _themeSubs.delete(force); }; }, []);
  return _themeName;
}

function getToken()    { return localStorage.getItem("cp_token"); }
function setToken(t)   { localStorage.setItem("cp_token", t); }
function clearToken()  { ["cp_token","cp_user","cp_admin","cp_partner"].forEach(k => localStorage.removeItem(k)); }
function authHeaders() { return { "Content-Type": "application/json", "Authorization": "Bearer " + getToken() }; }

// ── ICONS — inline line icons (Lucide-style), stroke = currentColor ──
const ICONS = {
  sun:       <><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/></>,
  moon:      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>,
  contrast:  <><circle cx="12" cy="12" r="9"/><path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" stroke="none"/></>,
  upload:    <><path d="M12 21V9"/><path d="M7 13l5-5 5 5"/><path d="M5 3h14"/></>,
  download:  <><path d="M12 3v12"/><path d="M7 11l5 5 5-5"/><path d="M5 21h14"/></>,
  trash:     <><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></>,
  package:   <><path d="M12 3 3 7.5v9L12 21l9-4.5v-9z"/><path d="M3 7.5 12 12l9-4.5M12 12v9"/></>,
  paperclip: <path d="M21 11l-8.5 8.5a4 4 0 0 1-6-6L14 5a2.5 2.5 0 0 1 4 4l-8.5 8.5a1 1 0 0 1-1.5-1.5L15 9"/>,
  clock:     <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
  lock:      <><rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></>,
  eye:       <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></>,
  refresh:   <><path d="M21 12a9 9 0 1 1-2.64-6.36"/><path d="M21 3v5h-5"/></>,
  users:     <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11"/></>,
  search:    <><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></>,
  info:      <><circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><path d="M12 8h.01"/></>,
  alert:     <><path d="M10.3 3.5 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.5a2 2 0 0 0-3.4 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></>,
  x:         <path d="M18 6 6 18M6 6l12 12"/>,
  check:     <path d="M20 6 9 17l-5-5"/>,
};
function Icon({ name, size = 16, strokeWidth = 1.75, style }) {
  const node = ICONS[name];
  if (!node) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ flexShrink:0, display:"inline-block", verticalAlign:"-0.15em", ...style }}>
      {node}
    </svg>
  );
}

// ── LOGIN ─────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err,      setErr]      = useState("");
  const [loading,  setLoading]  = useState(false);

  const doLogin = async () => {
    if (!username || !password) return setErr("Enter username and password");
    setLoading(true); setErr("");
    try {
      const r = await fetch(API + "/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const d = await r.json();
      if (!r.ok) return setErr(d.error || "Login failed");
      setToken(d.token);
      localStorage.setItem("cp_user",    d.displayName || d.username);
      localStorage.setItem("cp_admin",   d.isAdmin ? "1" : "0");
      localStorage.setItem("cp_staff",   d.isStaff ? "1" : "0");
      localStorage.setItem("cp_partner", d.partnerId || "");
      onLogin(d);
    } catch { setErr("Could not connect to server"); }
    finally  { setLoading(false); }
  };

  const iS = {
    width: "100%", background: T.inputBg, border: "1px solid " + T.border,
    borderRadius: 8, padding: "10px 12px", color: T.text, fontSize: 14,
    outline: "none", boxSizing: "border-box", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif",
  };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 380 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ width: 60, height: 60, background: T.accent, borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, fontWeight: 900, color: "#fff", margin: "0 auto 16px" }}>D</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.text }}>Dewey's Bakery</div>
          <div style={{ fontSize: 14, color: T.textMuted, marginTop: 4 }}>Co-Manufacturing Partner Portal</div>
        </div>
        <div style={{ background: T.surface, border: "1px solid " + T.border, borderRadius: 16, padding: 28, boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
          {err && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: RED, marginBottom: 16 }}>
              {err}
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: T.textMuted, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Username</label>
            <input style={iS} value={username} onChange={e => setUsername(e.target.value)} onKeyDown={e => e.key === "Enter" && doLogin()} autoFocus />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 11, color: T.textMuted, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Password</label>
            <input type="password" style={iS} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && doLogin()} />
          </div>
          <button
            onClick={doLogin} disabled={loading}
            style={{ width: "100%", background: T.accent, border: "none", borderRadius: 10, padding: "12px", color: "#fff", fontSize: 15, fontWeight: 700, cursor: loading ? "default" : "pointer", opacity: loading ? 0.7 : 1 }}
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </div>
        <div style={{ textAlign: "center", marginTop: 20, fontSize: 12, color: T.textMuted }}>
          Contact Dewey's Bakery to reset your password
        </div>
      </div>
    </div>
  );
}

// ── INVENTORY VIEW (co-man) ───────────────────────────────────────
function InventoryView({ previewPartnerId }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState("");
  const [sortCol, setSortCol] = useState("item_code");
  const [sortDir, setSortDir] = useState("asc");
  const [hideZero, setHideZero] = useState(true);
  const [showSummary, setShowSummary] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const url = previewPartnerId
        ? `${API}/api/inventory?partner_id=${previewPartnerId}`
        : `${API}/api/inventory`;
      const r = await fetch(url, { headers: authHeaders() });
      if (!r.ok) throw new Error((await r.json()).error);
      setData(await r.json());
    } catch(e) { setError(e.message); }
    setLoading(false);
  }, [previewPartnerId]);

  const caseMap = data?.caseMap || {};
  const toCases = (itemCode, units) => {
    const upc = caseMap[itemCode];
    if (!upc || upc <= 1) return null;
    return Math.floor(units / upc);
  };

  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    if (!data?.rows?.length) return;
    const headers = ["Item Code","Description","Lot Code","Warehouse","On Hand","In Shipping"];
    const csvRows = [
      headers.join(","),
      ...data.rows.map(r => [
r.item_code, `"${(r.description||"").replace(/"/g,'""')}"`, r.lot_code,
        r.warehouse,
        r.qty_on_hand, r.qty_in_shipping
      ].join(","))
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `inventory_${data.partner?.replace(/\s+/g,"_")}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const filtered = (data?.rows || []).filter(r => {
    if (hideZero && (r.qty_on_hand || 0) <= 0) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.item_code||"").toLowerCase().includes(s) ||
           (r.description||"").toLowerCase().includes(s) ||
           (r.lot_code||"").toLowerCase().includes(s);
  });

  // SKU summary — total on hand per item, SO counted once per item+warehouse
  const skuTotals = Object.values(
    (data?.rows || []).reduce((acc, r) => {
      const skuKey = r.item_code;
      const whKey  = r.item_code + "|" + r.warehouse;
      if (!acc[skuKey]) acc[skuKey] = { item_code: r.item_code, description: r.description, total_on_hand: 0, total_on_so: 0, _whSeen: new Set() };
      acc[skuKey].total_on_hand += r.qty_on_hand || 0;
      // Only add SO once per item+warehouse (it's a warehouse-level total duplicated across lot rows)
      if (!acc[skuKey]._whSeen.has(whKey)) {
        acc[skuKey]._whSeen.add(whKey);
        acc[skuKey].total_on_so += r.qty_on_sales_order || 0;
      }
      return acc;
    }, {})
  ).filter(s => !hideZero || s.total_on_hand > 0)
   .sort((a, b) => a.item_code.localeCompare(b.item_code))
   .map(s => ({ ...s, total_available: Math.max(0, s.total_on_hand - s.total_on_so) }));

  const sorted = [...filtered].sort((a, b) => {
    const va = a[sortCol] ?? ""; const vb = b[sortCol] ?? "";
    const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
    return sortDir === "asc" ? cmp : -cmp;
  });

  const sort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  const SortArrow = ({ col }) => sortCol === col ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  const qtyColor = (val, warn=0) => val <= 0 ? RED : val <= warn ? YELLOW : T.text;

  const thStyle = (col) => ({
    padding: "10px 12px", textAlign: col.startsWith("qty") ? "right" : "left",
    fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: "uppercase",
    letterSpacing: "0.06em", cursor: "pointer", whiteSpace: "nowrap",
    borderBottom: "2px solid " + T.border, userSelect: "none",
  });
  const tdStyle = (right=false) => ({
    padding: "10px 12px", fontSize: 13, color: T.text,
    textAlign: right ? "right" : "left", borderBottom: "1px solid " + T.border,
    whiteSpace: "nowrap",
  });

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.textMuted }}>Loading inventory…</div>;
  if (error)   return <div style={{ padding: 40, textAlign: "center", color: RED }}>{error}</div>;
  if (!data)   return null;

  // Totals
  // Derive top-level totals from skuTotals to avoid SO double-counting across lot rows
  const totals = {
    hand:  skuTotals.reduce((s, r) => s + r.total_on_hand, 0),
    so:    skuTotals.reduce((s, r) => s + r.total_on_so,   0),
    ship:  filtered.reduce((s, r)  => s + (r.qty_in_shipping || 0), 0),
    avail: skuTotals.reduce((s, r) => s + r.total_available, 0),
  };

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "On Hand",
            val: (() => {
              const totalCases = skuTotals.reduce((s, sk) => {
                const upc = caseMap[sk.item_code];
                return s + (upc && upc > 1 ? Math.floor(sk.total_on_hand / upc) : 0);
              }, 0);
              return (
                <div>
                  <div>{totals.hand.toLocaleString()} <span style={{ fontSize: 11, fontWeight: 400, color: T.textMuted }}>units</span></div>
                  {totalCases > 0 && <div style={{ fontSize: 14, color: T.textSub }}>{totalCases.toLocaleString()} <span style={{ fontSize: 11, fontWeight: 400, color: T.textMuted }}>cases</span></div>}
                </div>
              );
            })(),
            color: T.text },
          { label: "On Sales Order",
            val: (() => {
              const soCases = skuTotals.reduce((s, sk) => {
                const upc = caseMap[sk.item_code];
                return s + (upc && upc > 1 ? Math.floor(sk.total_on_so / upc) : 0);
              }, 0);
              return (
                <div>
                  <div>{totals.so.toLocaleString()} <span style={{ fontSize: 11, fontWeight: 400, color: T.textMuted }}>units</span></div>
                  {soCases > 0 && <div style={{ fontSize: 14, color: YELLOW }}>{soCases.toLocaleString()} <span style={{ fontSize: 11, fontWeight: 400, color: T.textMuted }}>cases</span></div>}
                </div>
              );
            })(),
            color: YELLOW },
          { label: "Available",
            val: (() => {
              const availCases = skuTotals.reduce((s, sk) => {
                const upc = caseMap[sk.item_code];
                return s + (upc && upc > 1 ? Math.floor(sk.total_available / upc) : 0);
              }, 0);
              return (
                <div>
                  <div>{totals.avail.toLocaleString()} <span style={{ fontSize: 11, fontWeight: 400, color: T.textMuted }}>units</span></div>
                  {availCases > 0 && <div style={{ fontSize: 14 }}>{availCases.toLocaleString()} <span style={{ fontSize: 11, fontWeight: 400, color: T.textMuted }}>cases</span></div>}
                </div>
              );
            })(),
            color: totals.avail > 0 ? GREEN : RED },

        ].map(c => (
          <div key={c.label} style={{ background: T.surface, border: "1px solid " + T.border, borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, textAlign: "center" }}>{c.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: c.color, fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", textAlign: "center" }}>
              {typeof c.val === "object" ? c.val : c.val.toLocaleString()}
            </div>
          </div>
        ))}
      </div>

      {/* Last import notice */}
      {data.lastUpdated && (
        <div style={{ textAlign: "right", fontSize: 11, color: T.textMuted, marginBottom: 8 }}>
          Last inventory import: <strong style={{ color: T.textSub }}>{new Date(data.lastUpdated).toLocaleString("en-US", { timeZone: "America/New_York" })} EST</strong>
        </div>
      )}

      {/* Search + download */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search item, description, or lot…"
          style={{ flex: 1, minWidth: 180, background: T.surface, border: "1px solid " + T.border, borderRadius: 8, padding: "8px 12px", fontSize: 13, color: T.text, outline: "none", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif" }}
        />
        <button
          onClick={() => setHideZero(h => !h)}
          style={{ background: hideZero ? T.accent : T.surface, border: "1px solid " + (hideZero ? T.accent : T.border), borderRadius: 8, padding: "8px 14px", color: hideZero ? "#fff" : T.textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
        >
          {hideZero ? "✓ Hiding Zero Stock" : "Show Zero Stock"}
        </button>
        <button
          onClick={() => setShowSummary(s => !s)}
          style={{ background: showSummary ? T.accent : T.surface, border: "1px solid " + (showSummary ? T.accent : T.border), borderRadius: 8, padding: "8px 14px", color: showSummary ? "#fff" : T.textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
        >
          {showSummary ? "✓ SKU Summary" : "SKU Summary"}
        </button>
        <button onClick={downloadCsv} style={{ background: T.accent, border: "none", borderRadius: 8, padding: "8px 16px", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
          ↓ CSV
        </button>
        <button onClick={load} style={{ background: T.surface, border: "1px solid " + T.border, borderRadius: 8, padding: "8px 12px", color: T.textSub, fontSize: 12, cursor: "pointer" }}>
          ↺
        </button>
      </div>

      {/* SKU Summary Table */}
      {showSummary && (
        <div style={{ background: T.surface, border: "1px solid " + T.border, borderRadius: 12, overflow: "hidden", marginBottom: 20 }}>
          <div style={{ padding: "10px 16px", borderBottom: "1px solid " + T.border, fontSize: 13, fontWeight: 700, color: T.text }}>
            SKU Summary — Total On Hand
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: T.bg }}>
                  {[["Item Code", false], ["Description", false], ["Total On Hand", true], ["On Sales Order", true], ["Available", true]].map(([label, right]) => (
                    <th key={label} style={{ padding: "9px 12px", fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: right ? "right" : "left", borderBottom: "2px solid " + T.border, whiteSpace: "nowrap" }}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {skuTotals.map((s, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : T.bg }}>
                    <td style={{ padding: "8px 12px", fontSize: 12, fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontWeight: 600, color: T.text, borderBottom: "1px solid " + T.border }}>{s.item_code}</td>
                    <td style={{ padding: "8px 12px", fontSize: 12, color: T.text, borderBottom: "1px solid " + T.border, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.description}</td>
                    <td style={{ padding: "8px 12px", fontSize: 12, fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", textAlign: "right", color: T.text, borderBottom: "1px solid " + T.border }}>
                      <div>{s.total_on_hand.toLocaleString()} <span style={{ fontSize: 10, color: T.textMuted }}>units</span></div>
                      {caseMap[s.item_code] > 1 && (
                        <div style={{ fontSize: 11, color: T.textSub }}>{Math.floor(s.total_on_hand / caseMap[s.item_code]).toLocaleString()} <span style={{ fontSize: 10, color: T.textMuted }}>cases</span></div>
                      )}
                    </td>
                    <td style={{ padding: "8px 12px", fontSize: 12, fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", textAlign: "right", color: s.total_on_so > 0 ? YELLOW : T.textMuted, borderBottom: "1px solid " + T.border }}>
                      <div>{s.total_on_so.toLocaleString()} <span style={{ fontSize: 10, color: T.textMuted }}>units</span></div>
                      {caseMap[s.item_code] > 1 && s.total_on_so > 0 && (
                        <div style={{ fontSize: 11 }}>{Math.floor(s.total_on_so / caseMap[s.item_code]).toLocaleString()} <span style={{ fontSize: 10, color: T.textMuted }}>cases</span></div>
                      )}
                    </td>
                    <td style={{ padding: "8px 12px", fontSize: 12, fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", textAlign: "right", fontWeight: 700, color: s.total_available > 0 ? GREEN : RED, borderBottom: "1px solid " + T.border }}>
                      <div>{s.total_available.toLocaleString()} <span style={{ fontSize: 10, fontWeight: 400, color: T.textMuted }}>units</span></div>
                      {caseMap[s.item_code] > 1 && (
                        <div style={{ fontSize: 11 }}>{Math.floor(s.total_available / caseMap[s.item_code]).toLocaleString()} <span style={{ fontSize: 10, fontWeight: 400, color: T.textMuted }}>cases</span></div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: "8px 16px", borderTop: "1px solid " + T.border, fontSize: 11, color: T.textMuted }}>
            {skuTotals.length} SKU{skuTotals.length !== 1 ? "s" : ""}
          </div>
        </div>
      )}

      {/* Warehouse legend */}
      <div style={{ background: T.surface, border: "1px solid " + T.border, borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: T.textMuted, display: "flex", gap: 20, flexWrap: "wrap" }}>
        <span><strong style={{ color: T.textSub }}>014</strong> — Wholesale / Grocery Orders</span>
        <span><strong style={{ color: T.textSub }}>022</strong> — Direct-to-Consumer Orders</span>
        <span><strong style={{ color: T.textSub }}>098</strong> — On Hold</span>
      </div>

      {sorted.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: T.textMuted, fontSize: 14 }}>
          {search ? "No items match your search" : "No inventory data — waiting for upload from Dewey's Bakery"}
        </div>
      ) : (
        <div style={{ background: T.surface, border: "1px solid " + T.border, borderRadius: 12, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: T.bg }}>
                  {[
                    ["item_code",          "Item Code"],
                    ["description",        "Description"],
                    ["lot_code",           "Lot Code"],
                    ["warehouse",          "Warehouse"],
                    ["qty_on_hand",        "On Hand"],
                    ["qty_in_shipping",    "In Shipping"],
                  ].map(([col, label]) => (
                    <th key={col} style={thStyle(col)} onClick={() => sort(col)}>
                      {label}<SortArrow col={col} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r, i) => {
                  return (
                    <tr key={i} style={{ background: "transparent" }}>
                      <td style={{ ...tdStyle(), fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 12, fontWeight: 600 }}>{r.item_code}</td>
                      <td style={{ ...tdStyle(), maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>{r.description}</td>
                      <td style={{ ...tdStyle(), fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 12 }}>{r.lot_code || "—"}</td>
                      <td style={{ ...tdStyle() }}>{r.warehouse || "—"}</td>
                      <td style={{ ...tdStyle(true), fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif" }}>
                        <div>{(r.qty_on_hand||0).toLocaleString()} <span style={{ fontSize: 10, color: T.textMuted }}>units</span></div>
                        {toCases(r.item_code, r.qty_on_hand||0) !== null && (
                          <div style={{ fontSize: 11, color: T.textSub }}>{toCases(r.item_code, r.qty_on_hand||0).toLocaleString()} <span style={{ fontSize: 10, color: T.textMuted }}>cases</span></div>
                        )}
                      </td>
                      <td style={{ ...tdStyle(true), fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", color: TEAL }}>{(r.qty_in_shipping||0).toLocaleString()}</td>

                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ padding: "10px 16px", borderTop: "1px solid " + T.border, fontSize: 11, color: T.textMuted, display: "flex", justifyContent: "space-between" }}>
            <span>{sorted.length} line{sorted.length !== 1 ? "s" : ""}{search ? ` (filtered from ${data.rows.length})` : ""}</span>
            {data.lastUpdated && <span>Last updated: {new Date(data.lastUpdated.replace(" ", "T") + "Z").toLocaleString("en-US", { timeZone: "America/New_York", month: "numeric", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true })} EST</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── UPLOAD PANEL (admin) ──────────────────────────────────────────
function UploadPanel() {
  const [status,   setStatus]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [uploads,  setUploads]  = useState([]);
  const [binFile,  setBinFile]  = useState(null);
  const [soFile,   setSoFile]   = useState(null);
  const [colMap,   setColMap]   = useState({
    item_code:           "Item Code",
    description:         "Description",
    lot_code:            "Lot Number",
    expiration_date:     "Expiration Date",
    warehouse:           "Warehouse",
    qty_on_hand:         "Quantity On Hand",
    qty_on_sales_order:  "Quantity On Sales Order",
    qty_in_shipping:     "Quantity In Shipping",
    qty_available:       "Quantity Available",
    partner_tag:         "Warehouse",
  });

  const refreshUploads = () =>
    fetch(API + "/api/uploads", { headers: authHeaders() })
      .then(r => r.json()).then(setUploads).catch(() => {});

  useEffect(() => { refreshUploads(); }, []);

  const deleteUpload = async (id) => {
    if (!window.confirm("Delete this upload record? This will remove all inventory rows from this import.")) return;
    await fetch(API + "/api/uploads/" + id, { method: "DELETE", headers: authHeaders() });
    refreshUploads();
  };

  const deleteOldUploads = async () => {
    if (!window.confirm("Delete all upload records older than 2 weeks? Their inventory rows will also be removed.")) return;
    await fetch(API + "/api/uploads/cleanup", { method: "DELETE", headers: authHeaders() });
    refreshUploads();
  };

  const parseStockStatusCSV = async (file) => {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter(l => l.trim());

    const parseCSVLine = (line) => {
      const result = []; let cur = ""; let inQuote = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQuote && line[i+1] === '"') { cur += '"'; i++; }
          else inQuote = !inQuote;
        } else if (ch === "," && !inQuote) {
          result.push(cur.trim()); cur = "";
        } else { cur += ch; }
      }
      result.push(cur.trim());
      return result;
    };

    const parseQty = (v) => parseFloat(String(v || "0").replace(/,/g, "")) || 0;
    const rows = [];

    for (const line of lines) {
      const cols = parseCSVLine(line);
      if (!cols[0] || cols[0].startsWith("Item ") || !cols[10]) continue;
      if (cols[0].startsWith("COM")) continue;

      const parts = cols[0].split("  ", 2);
      const itemCode = parts[0].trim();
      if (!itemCode) continue;

      rows.push({
        item_code:          itemCode,
        warehouse:          cols[10] || "",
        qty_on_sales_order: parseQty(cols[13]),
        qty_available:      parseQty(cols[15]),
      });
    }
    return rows;
  };

  const handleImport = async () => {
    if (!binFile) return setStatus({ type: "error", msg: "Please select the Bin Location Stock Status Report (CSV)" });
    setLoading(true); setStatus(null);

    try {
      const file = binFile;
      const isCsv = file.name.toLowerCase().endsWith(".csv");
      let rows = [];

      if (isCsv) {
        const text = await file.text();
        const lines = text.split(/\r?\n/).filter(l => l.trim());

        const parseCSVLine = (line) => {
          const result = []; let cur = ""; let inQuote = false;
          for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
              if (inQuote && line[i+1] === '"') { cur += '"'; i++; }
              else inQuote = !inQuote;
            } else if (ch === "," && !inQuote) {
              result.push(cur.trim()); cur = "";
            } else { cur += ch; }
          }
          result.push(cur.trim());
          return result;
        };

        const parseQty = (v) => parseFloat(String(v || "0").replace(/,/g, "")) || 0;

        // Detect which CSV format by inspecting the first data row
        const firstCols = parseCSVLine(lines[0] || "");
        const isBinLoc = firstCols[2] === "Product Line";

        if (isBinLoc) {
          // ── Bin Location Stock Status Report ──────────────────
          // col1  = "ITEMCODE    Description" (split on 4 spaces)
          // col2  = "Product Line" (identifies data rows)
          // col4  = partner tag (e.g. CMAN)
          // col8  = lot number
          // col10 = expiration date
          // col11 = qty on hand
          // col14 = qty on sales order
          // col18 = qty available
          // col30 = "Warehouse 014 Total:" (extract warehouse number)

          for (const line of lines) {
            const cols = parseCSVLine(line);
            if (cols[2] !== "Product Line") continue;

            const parts = (cols[1] || "").split("    ", 2);
            const itemCode = parts[0].trim();
            const description = parts[1] ? parts[1].trim() : "";

            if (!itemCode || itemCode.startsWith("COM")) continue;

            const whMatch = (cols[30] || "").match(/Warehouse (\w+) Total/);
            const warehouse = whMatch ? whMatch[1] : "";
            const partnerTag = cols[4] || "";

            rows.push({
              item_code:          itemCode,
              description:        (partnerTag ? partnerTag + " " : "") + description,
              lot_code:           cols[10] || "",
              expiration_date:    cols[10] || "",
              warehouse:          warehouse,
              qty_on_hand:        parseQty(cols[11]),
              qty_on_sales_order: parseQty(cols[14]),
              qty_in_shipping:    0,
              qty_available:      parseQty(cols[18]),
            });
          }

        } else {
          // ── Sage Valuation Report CSV parser ──────────────────────
        // No header row. Two row layouts:
        //
        // Lot items (col18 = "Lot"):
        //   col0=item_code, col1=description, col16=partner_tag (first row only),
        //   col20=warehouse, col21=qty_on_hand
        //
        // Standard Cost items (col6 = "Standard Cost"):
        //   col0=item_code, col1=description, col4=partner_tag,
        //   col8=warehouse, col12=qty_on_hand
        //
        // Rows are duplicated from merged-cell export — deduplicate on (item, warehouse, qty).

        const text = await file.text();
        const lines = text.split(/\r?\n/).filter(l => l.trim());

        const parseCSVLine = (line) => {
          const result = []; let cur = ""; let inQuote = false;
          for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
              if (inQuote && line[i+1] === '"') { cur += '"'; i++; }
              else inQuote = !inQuote;
            } else if (ch === "," && !inQuote) {
              result.push(cur.trim()); cur = "";
            } else { cur += ch; }
          }
          result.push(cur.trim());
          return result;
        };

        const parseQty = (v) => parseFloat(String(v || "0").replace(/,/g, "")) || 0;

        const seen = new Set();
        const itemPartnerTag = {};
        const rawRows = [];

        for (const line of lines) {
          const cols = parseCSVLine(line);
          if (cols.length < 10) continue;

          const itemCode = cols[0];
          if (!itemCode || itemCode.startsWith("Item ")) continue;

          const isLot     = cols[18] === "Lot";
          const isStdCost = cols[6]  === "Standard Cost";
          if (!isLot && !isStdCost) continue;

          const warehouse  = isLot ? cols[20] : cols[8];
          const qty        = parseQty(isLot ? cols[21] : cols[12]);
          const partnerTag = isLot ? cols[16] : cols[4];

          if (!warehouse) continue;
          if (partnerTag && !itemPartnerTag[itemCode]) itemPartnerTag[itemCode] = partnerTag;

          const key = `${itemCode}|${warehouse}|${qty}`;
          if (seen.has(key)) continue;
          seen.add(key);

          rawRows.push({ itemCode, description: cols[1] || "", warehouse, qty, tag: partnerTag });
        }

        // Build final rows, back-filling partner tag on secondary warehouse rows
        rows = rawRows.map(r => ({
          item_code:          r.itemCode,
          description:        (itemPartnerTag[r.itemCode] ? itemPartnerTag[r.itemCode] + " " : "") + r.description,
          lot_code:           "",
          expiration_date:    "",
          warehouse:          r.warehouse,
          qty_on_hand:        r.qty,
          qty_on_sales_order: 0,
          qty_in_shipping:    0,
          qty_available:      r.qty,
        }));

        } // end valuation report else

      } else {
        // ── Standard flat XLSX parser ─────────────────────────────
        if (!window.XLSX) {
          await new Promise((res, rej) => {
            const s = document.createElement("script");
            s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
            s.onload = res; s.onerror = rej; document.head.appendChild(s);
          });
        }

        const ab = await file.arrayBuffer();
        const wb = window.XLSX.read(ab, { type: "array", cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rawRows = window.XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: false });

        if (rawRows.length < 2) throw new Error("File appears empty");

        let headerRowIdx = 0;
        for (let i = 0; i < Math.min(10, rawRows.length); i++) {
          const nonNull = rawRows[i].filter(c => c != null).length;
          if (nonNull >= 4) { headerRowIdx = i; break; }
        }

        const headers = rawRows[headerRowIdx].map(h => (h||"").toString().trim());

        const findCol = (...keywords) => {
          for (const kw of keywords) {
            const idx = headers.findIndex(h => h.toLowerCase().includes(kw.toLowerCase()));
            if (idx >= 0) return idx;
          }
          return -1;
        };

        const colIdx = {
          item_code:          findCol("item code", "item no", "sku", "part"),
          description:        findCol("description", "desc", "item description"),
          lot_code:           findCol("lot number", "lot no", "lot code", "lot"),
          expiration_date:    findCol("expir", "exp date", "best by", "use by"),
          warehouse:          findCol("warehouse", "whse", "location", "wh"),
          qty_on_hand:        findCol("on hand", "quantity on hand", "qty on hand", "qoh"),
          qty_on_sales_order: findCol("sales order", "on order", "allocated", "committed"),
          qty_in_shipping:    findCol("shipping", "in ship", "staged", "pick"),
          qty_available:      findCol("available", "avail"),
        };

        for (let i = headerRowIdx + 1; i < rawRows.length; i++) {
          const r = rawRows[i];
          if (!r || r.every(c => c == null)) continue;

          const getVal = (field) => {
            const idx = colIdx[field];
            if (idx < 0 || idx >= r.length) return null;
            const v = r[idx];
            return v == null ? null : String(v).trim();
          };

          const itemCode = getVal("item_code");
          if (!itemCode) continue;

          rows.push({
            item_code:          itemCode,
            description:        getVal("description") || "",
            lot_code:           getVal("lot_code")    || "",
            expiration_date:    getVal("expiration_date") || "",
            warehouse:          getVal("warehouse")   || "",
            qty_on_hand:        parseFloat(getVal("qty_on_hand"))        || 0,
            qty_on_sales_order: parseFloat(getVal("qty_on_sales_order")) || 0,
            qty_in_shipping:    parseFloat(getVal("qty_in_shipping"))    || 0,
            qty_available:      parseFloat(getVal("qty_available"))      || 0,
          });
        }
      }

      if (rows.length === 0) throw new Error("No data rows found — check the file is a Sage Valuation Report CSV or a standard inventory export");

      // Parse Stock Status report if provided
      let soRows = [];
      if (soFile) {
        soRows = await parseStockStatusCSV(soFile);
      }

      // Send to server
      const resp = await fetch(API + "/api/upload", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ rows, soRows, filename: file.name + (soFile ? " + " + soFile.name : "") }),
      });
      const result = await resp.json();
      if (!resp.ok) throw new Error(result.error);

      setStatus({ type: "success", msg: `✓ Imported ${result.matchedRows} rows across ${result.partnersUpdated} partner${result.partnersUpdated !== 1 ? "s" : ""} (${result.totalRows} total rows in file)${soRows.length ? ` · Sales order data from Stock Status report applied` : ""}` });
      setBinFile(null); setSoFile(null);

      // Refresh uploads list
      fetch(API + "/api/uploads", { headers: authHeaders() })
        .then(r => r.json()).then(setUploads).catch(() => {});

    } catch(err) {
      setStatus({ type: "error", msg: "Import failed: " + err.message });
    }
    setLoading(false);
  };

  return (
    <div>
      <div style={{ background: T.surface, border: "1px solid " + T.border, borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4 }}>Upload Sage Inventory Report</div>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16 }}>
          Upload the Sage Inventory Valuation Report as a CSV, or any flat Excel inventory export. Rows are matched to partners by their assigned tags.
        </div>

        {status && (
          <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 12,
            background: status.type === "success" ? "#f0fdf4" : "#fef2f2",
            border: "1px solid " + (status.type === "success" ? "#bbf7d0" : "#fecaca"),
            color: status.type === "success" ? "#15803d" : RED }}>
            {status.msg}
          </div>
        )}

        {/* File 1 - Bin Location */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.textSub, marginBottom: 6 }}>
            1. Bin Location Stock Status Report <span style={{ color: T.textMuted, fontWeight: 400 }}>(required — provides lot codes &amp; on hand)</span>
          </div>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, background: binFile ? "#f0fdf4" : T.bg, border: "1px solid " + (binFile ? "#86efac" : T.border), borderRadius: 8, padding: "9px 16px", cursor: loading ? "default" : "pointer", fontSize: 13, color: binFile ? "#15803d" : T.textSub }}>
            {binFile ? "✓ " + binFile.name : <><Icon name="paperclip" size={13} style={{ verticalAlign:"-0.1em", marginRight:5 }} />Choose Bin Location CSV</>}
            <input type="file" accept=".csv" style={{ display: "none" }} onChange={e => setBinFile(e.target.files?.[0] || null)} disabled={loading} />
          </label>
        </div>

        {/* File 2 - Stock Status */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.textSub, marginBottom: 6 }}>
            2. Inventory Stock Status Report <span style={{ color: T.textMuted, fontWeight: 400 }}>(optional — provides accurate On Sales Order quantities)</span>
          </div>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, background: soFile ? "#f0fdf4" : T.bg, border: "1px solid " + (soFile ? "#86efac" : T.border), borderRadius: 8, padding: "9px 16px", cursor: loading ? "default" : "pointer", fontSize: 13, color: soFile ? "#15803d" : T.textSub }}>
            {soFile ? "✓ " + soFile.name : <><Icon name="paperclip" size={13} style={{ verticalAlign:"-0.1em", marginRight:5 }} />Choose Stock Status CSV</>}
            <input type="file" accept=".csv" style={{ display: "none" }} onChange={e => setSoFile(e.target.files?.[0] || null)} disabled={loading} />
          </label>
          {soFile && <button onClick={() => setSoFile(null)} style={{ marginLeft: 8, background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 18 }}>×</button>}
        </div>

        <button
          onClick={handleImport}
          disabled={loading || !binFile}
          style={{ background: T.accent, border: "none", borderRadius: 8, padding: "10px 24px", color: "#fff", fontSize: 13, fontWeight: 600, cursor: (loading || !binFile) ? "default" : "pointer", opacity: (loading || !binFile) ? 0.6 : 1 }}
        >
          {loading ? "Importing…" : <><Icon name="upload" size={14} style={{ verticalAlign:"-0.15em", marginRight:5 }} />Import Inventory</>}
        </button>

        <div style={{ marginTop: 16, padding: "12px 14px", background: T.bg, borderRadius: 8, fontSize: 11, color: T.textMuted }}>
          <strong style={{ color: T.textSub }}>Tip:</strong> Upload both reports together for the most complete data — lot codes from the Bin Location report, sales order quantities from the Stock Status report.
          <br /><br />
          <strong style={{ color: T.textSub }}>Partner matching:</strong> Rows are assigned to partners based on their tags (item code prefix, product line, etc.).
        </div>
      </div>

      {/* Upload history */}
      {uploads.length > 0 && (
        <div style={{ background: T.surface, border: "1px solid " + T.border, borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid " + T.border, fontSize: 13, fontWeight: 700, color: T.text, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Upload History</span>
            <button onClick={deleteOldUploads} style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "4px 12px", color: RED, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              <Icon name="trash" size={13} style={{ verticalAlign:"-0.1em", marginRight:5 }} />Delete Older Than 2 Weeks
            </button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: T.bg }}>
                {["Date", "File", "Uploaded By", "Rows", "Partners", ""].map(h => (
                  <th key={h} style={{ padding: "8px 12px", fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", textAlign: "left", borderBottom: "1px solid " + T.border }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {uploads.map(u => (
                <tr key={u.id}>
                  <td style={{ padding: "8px 12px", fontSize: 12, color: T.text, borderBottom: "1px solid " + T.border }}>{new Date(u.uploaded_at.replace(" ", "T") + "Z").toLocaleString("en-US", { timeZone: "America/New_York", month: "numeric", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true })} EST</td>
                  <td style={{ padding: "8px 12px", fontSize: 12, color: T.textSub, borderBottom: "1px solid " + T.border }}>{u.filename}</td>
                  <td style={{ padding: "8px 12px", fontSize: 12, color: T.textSub, borderBottom: "1px solid " + T.border }}>{u.uploaded_by}</td>
                  <td style={{ padding: "8px 12px", fontSize: 12, color: T.text,    borderBottom: "1px solid " + T.border }}>{u.row_count?.toLocaleString()}</td>
                  <td style={{ padding: "8px 12px", fontSize: 12, color: T.text,    borderBottom: "1px solid " + T.border }}>{u.partner_count}</td>
                  <td style={{ padding: "8px 12px", borderBottom: "1px solid " + T.border }}>
                    <button onClick={() => deleteUpload(u.id)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 16, lineHeight: 1 }} title="Delete this upload">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── SKU CONFIG PANEL ─────────────────────────────────────────────
function SkuConfigPanel({ partnerId, partnerName }) {
  const [skus,    setSkus]    = useState([]);
  const [editing, setEditing] = useState({});
  const [saving,  setSaving]  = useState({});
  const [msg,     setMsg]     = useState(null);

  useEffect(() => {
    if (!partnerId) return;
    fetch(`${API}/api/sku-configs/${partnerId}`, { headers: authHeaders() })
      .then(r => r.json()).then(rows => {
        setSkus(rows);
        const init = {};
        rows.forEach(r => { init[r.id] = String(r.units_per_case || 1); });
        setEditing(init);
      }).catch(() => {});
  }, [partnerId]);

  const save = async (id) => {
    const val = parseFloat(editing[id]);
    if (!val || val <= 0) return;
    setSaving(s => ({ ...s, [id]: true }));
    await fetch(`${API}/api/sku-configs/${id}`, {
      method: "PATCH", headers: authHeaders(),
      body: JSON.stringify({ units_per_case: val })
    });
    setSkus(prev => prev.map(s => s.id === id ? { ...s, units_per_case: val } : s));
    setSaving(s => ({ ...s, [id]: false }));
    setMsg("Saved");
    setTimeout(() => setMsg(null), 2000);
  };

  const toggleFinished = async (id, next) => {
    setSkus(prev => prev.map(s => s.id === id ? { ...s, is_finished_good: next ? 1 : 0 } : s));
    await fetch(`${API}/api/sku-configs/${id}`, {
      method: "PATCH", headers: authHeaders(),
      body: JSON.stringify({ is_finished_good: next ? 1 : 0 })
    });
    setMsg("Saved");
    setTimeout(() => setMsg(null), 2000);
  };

  if (!partnerId) return <div style={{ padding: 20, color: "#7a9e79", fontSize: 13 }}>Select a partner to configure SKUs.</div>;
  if (skus.length === 0) return <div style={{ padding: 20, color: "#7a9e79", fontSize: 13 }}>No SKUs found — upload inventory first to populate SKUs.</div>;

  return (
    <div>
      <div style={{ fontSize: 12, color: "#7a9e79", marginBottom: 12 }}>
        Set the units per case for each SKU. Case counts will be calculated automatically from on-hand quantities.
        Uncheck <strong>Finished Good</strong> to treat a SKU as an ingredient/supply — it will be hidden from the portal inventory view.
      </div>
      {msg && <div style={{ fontSize: 12, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "6px 12px", marginBottom: 10 }}>{msg}</div>}
      <div style={{ background: "#fff", border: "1px solid #d4e4d3", borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f4f7f4" }}>
              {["Item Code", "Description", "Finished Good", "Units / Case", ""].map(h => (
                <th key={h} style={{ padding: "8px 12px", fontSize: 10, fontWeight: 700, color: "#7a9e79", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "left", borderBottom: "2px solid #d4e4d3" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {skus.map((s, i) => (
              <tr key={s.id} style={{ background: i % 2 === 0 ? "transparent" : "#f4f7f4" }}>
                <td style={{ padding: "8px 12px", fontSize: 12, fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontWeight: 600, color: "#1a2e1a", borderBottom: "1px solid #d4e4d3" }}>{s.item_code}</td>
                <td style={{ padding: "8px 12px", fontSize: 12, color: "#4a6b49", borderBottom: "1px solid #d4e4d3", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.description}</td>
                <td style={{ padding: "8px 12px", borderBottom: "1px solid #d4e4d3", width: 110, textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={s.is_finished_good !== 0}
                    onChange={e => toggleFinished(s.id, e.target.checked)}
                    style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#5C8A5A" }}
                  />
                </td>
                <td style={{ padding: "8px 12px", borderBottom: "1px solid #d4e4d3", width: 120 }}>
                  <input
                    type="number" min="1" step="1"
                    value={editing[s.id] || ""}
                    onChange={e => setEditing(v => ({ ...v, [s.id]: e.target.value }))}
                    onKeyDown={e => e.key === "Enter" && save(s.id)}
                    style={{ width: "100%", background: "#f4f7f4", border: "1px solid #d4e4d3", borderRadius: 5, padding: "5px 8px", fontSize: 13, fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", color: "#1a2e1a", outline: "none" }}
                  />
                </td>
                <td style={{ padding: "8px 12px", borderBottom: "1px solid #d4e4d3", width: 70 }}>
                  <button
                    onClick={() => save(s.id)}
                    disabled={saving[s.id]}
                    style={{ background: "#5C8A5A", border: "none", borderRadius: 6, padding: "4px 12px", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                  >
                    {saving[s.id] ? "…" : "Save"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── PARTNERS PANEL (admin) ────────────────────────────────────────
function PartnersPanel() {
  const [partners, setPartners] = useState([]);
  const [editId,   setEditId]   = useState(null);
  const [form,     setForm]     = useState({ name: "", tags: "", notes: "" });
  const [msg,      setMsg]      = useState(null);
  const [skuPartner, setSkuPartner] = useState(null);

  const load = () => {
    fetch(API + "/api/partners", { headers: authHeaders() })
      .then(r => r.json()).then(setPartners).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return setMsg({ type: "error", text: "Partner name required" });
    try {
      if (editId === "new") {
        await fetch(API + "/api/partners", { method: "POST", headers: authHeaders(), body: JSON.stringify(form) });
      } else {
        await fetch(API + "/api/partners/" + editId, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(form) });
      }
      setEditId(null); setForm({ name: "", tags: "", notes: "" }); load();
    } catch { setMsg({ type: "error", text: "Save failed" }); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete partner "${name}" and all their inventory? This cannot be undone.`)) return;
    await fetch(API + "/api/partners/" + id, { method: "DELETE", headers: authHeaders() });
    load();
  };

  const iS = { width: "100%", background: T.inputBg, border: "1px solid " + T.border, borderRadius: 6, padding: "7px 10px", color: T.text, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", marginBottom: 8 };

  return (
    <div>
      {msg && (
        <div style={{ padding: "8px 14px", borderRadius: 8, marginBottom: 12, fontSize: 12, background: msg.type === "error" ? "#fef2f2" : "#f0fdf4", color: msg.type === "error" ? RED : GREEN }}>
          {msg.text}
        </div>
      )}

      <button
        onClick={() => { setEditId("new"); setForm({ name: "", tags: "", notes: "" }); }}
        style={{ background: SAGE, border: "none", borderRadius: 8, padding: "8px 16px", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", marginBottom: 16 }}
      >
        + Add Partner
      </button>

      {editId && (
        <div style={{ background: T.surface, border: "1px solid " + T.accent, borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>{editId === "new" ? "New Partner" : "Edit Partner"}</div>
          <label style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase" }}>Partner Name</label>
          <input style={iS} value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="e.g. Callie's Biscuits" />
          <label style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase" }}>
            Match Tags <span style={{ fontWeight: 400, textTransform: "none" }}>(comma-separated — used to match rows in the Sage export)</span>
          </label>
          <input style={iS} value={form.tags} onChange={e => setForm(f => ({...f, tags: e.target.value}))} placeholder="e.g. CAL, 016, CALLIE" />
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8 }}>
            Rows in the Sage export are assigned to this partner if their Item Code, Description, or Warehouse contains any of these tags.
          </div>
          <label style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase" }}>Notes (internal)</label>
          <input style={iS} value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Optional internal notes" />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={save} style={{ background: SAGE, border: "none", borderRadius: 8, padding: "8px 16px", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Save</button>
            <button onClick={() => setEditId(null)} style={{ background: "none", border: "1px solid " + T.border, borderRadius: 8, padding: "8px 16px", color: T.textMuted, fontSize: 13, cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {partners.length === 0 && (
          <div style={{ textAlign: "center", padding: 40, color: T.textMuted, fontSize: 13 }}>No partners yet — add your first partner above</div>
        )}
        {partners.map(p => (
          <div key={p.id} style={{ background: T.surface, border: "1px solid " + T.border, borderRadius: 10, overflow: "hidden" }}>
            <div style={{ padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 2 }}>{p.name}</div>
                <div style={{ fontSize: 12, color: T.textMuted }}>
                  Tags: <span style={{ color: T.textSub, fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif" }}>{p.tags || "none"}</span>
                  &nbsp;·&nbsp; {p.userCount} user{p.userCount !== 1 ? "s" : ""}
                  &nbsp;·&nbsp; {p.item_count || 0} inventory lines
                </div>
                {p.notes && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{p.notes}</div>}
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button onClick={() => setSkuPartner(skuPartner === p.id ? null : p.id)}
                  style={{ background: skuPartner === p.id ? T.accent : T.bg, border: "1px solid " + (skuPartner === p.id ? T.accent : T.border), borderRadius: 6, padding: "5px 10px", color: skuPartner === p.id ? "#fff" : T.textSub, fontSize: 12, cursor: "pointer" }}>
                  <Icon name="package" size={14} style={{ verticalAlign:"-0.15em", marginRight:5 }} />SKUs
                </button>
                <button onClick={() => { setEditId(p.id); setForm({ name: p.name, tags: p.tags, notes: p.notes||"" }); }}
                  style={{ background: T.bg, border: "1px solid " + T.border, borderRadius: 6, padding: "5px 10px", color: T.textSub, fontSize: 12, cursor: "pointer" }}>Edit</button>
                <button onClick={() => del(p.id, p.name)}
                  style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "5px 10px", color: RED, fontSize: 12, cursor: "pointer" }}>Delete</button>
              </div>
            </div>
            {skuPartner === p.id && (
              <div style={{ borderTop: "1px solid " + T.border, padding: "14px 16px", background: T.bg }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 10 }}>SKU Configuration — Case Packs &amp; Finished-Good Filter</div>
                <SkuConfigPanel partnerId={p.id} partnerName={p.name} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── USERS PANEL (admin) ───────────────────────────────────────────
function UsersPanel() {
  const [users,    setUsers]    = useState([]);
  const [partners, setPartners] = useState([]);
  const [showNew,  setShowNew]  = useState(false);
  const [form,     setForm]     = useState({ username: "", password: "", displayName: "", partnerId: "", isStaff: false });
  const [resetPw,  setResetPw]  = useState({});
  const [msg,      setMsg]      = useState(null);

  const load = () => {
    fetch(API + "/api/users",    { headers: authHeaders() }).then(r => r.json()).then(setUsers).catch(() => {});
    fetch(API + "/api/partners", { headers: authHeaders() }).then(r => r.json()).then(setPartners).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const createUser = async () => {
    if (!form.username || !form.password) return setMsg({ type: "error", text: "Username and password required" });
    const r = await fetch(API + "/api/users", { method: "POST", headers: authHeaders(), body: JSON.stringify({ ...form, isStaff: form.isStaff || false }) });
    const d = await r.json();
    if (!r.ok) return setMsg({ type: "error", text: d.error });
    setShowNew(false); setForm({ username: "", password: "", displayName: "", partnerId: "" }); load();
  };

  const doResetPw = async (userId) => {
    const pw = resetPw[userId];
    if (!pw || pw.length < 8) return setMsg({ type: "error", text: "Password must be at least 8 characters" });
    await fetch(API + "/api/users/" + userId, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ password: pw }) });
    setResetPw(p => ({...p, [userId]: ""}));
    setMsg({ type: "success", text: "Password updated" });
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Delete user "${name}"?`)) return;
    await fetch(API + "/api/users/" + id, { method: "DELETE", headers: authHeaders() });
    load();
  };

  const iS = { background: T.inputBg, border: "1px solid " + T.border, borderRadius: 6, padding: "6px 10px", color: T.text, fontSize: 12, outline: "none", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif" };

  return (
    <div>
      {msg && (
        <div style={{ padding: "8px 14px", borderRadius: 8, marginBottom: 12, fontSize: 12, background: msg.type === "error" ? "#fef2f2" : "#f0fdf4", color: msg.type === "error" ? RED : GREEN }}>
          {msg.text}
        </div>
      )}

      <button onClick={() => setShowNew(true)} style={{ background: SAGE, border: "none", borderRadius: 8, padding: "8px 16px", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", marginBottom: 16 }}>
        + Add User
      </button>

      {showNew && (
        <div style={{ background: T.surface, border: "1px solid " + T.accent, borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>New User</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
            <div><div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>Username</div><input style={{...iS, width: "100%"}} value={form.username} onChange={e => setForm(f=>({...f,username:e.target.value}))} placeholder="username" /></div>
            <div><div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>Password</div><input type="password" style={{...iS, width: "100%"}} value={form.password} onChange={e => setForm(f=>({...f,password:e.target.value}))} placeholder="min 8 chars" /></div>
            <div><div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>Display Name</div><input style={{...iS, width: "100%"}} value={form.displayName} onChange={e => setForm(f=>({...f,displayName:e.target.value}))} placeholder="e.g. Jane at Callie's" /></div>
            <div>
              <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>Partner</div>
              <select style={{...iS, width: "100%"}} value={form.partnerId} onChange={e => setForm(f=>({...f,partnerId:e.target.value}))}>
                <option value="">— No partner (admin) —</option>
                {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          <div
            onClick={() => setForm(f=>({...f,isStaff:!f.isStaff}))}
            style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, padding: "10px 12px", borderRadius: 8, border: "1px solid " + (form.isStaff ? T.accent : T.border), background: form.isStaff ? "#f0fdf4" : T.bg, cursor: "pointer" }}
          >
            <input type="checkbox" checked={form.isStaff||false} onChange={e => setForm(f=>({...f,isStaff:e.target.checked}))} onClick={e => e.stopPropagation()} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>Dewey's Staff Account</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>Can view all partner inventory and activity — cannot upload files or manage users/partners</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={createUser} style={{ background: SAGE, border: "none", borderRadius: 8, padding: "8px 16px", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Create User</button>
            <button onClick={() => setShowNew(false)} style={{ background: "none", border: "1px solid " + T.border, borderRadius: 8, padding: "8px 16px", color: T.textMuted, fontSize: 13, cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ background: T.surface, border: "1px solid " + T.border, borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: T.bg }}>
              {["User", "Partner", "Role", "Reset Password", ""].map(h => (
                <th key={h} style={{ padding: "10px 12px", fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", textAlign: "left", borderBottom: "1px solid " + T.border }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td style={{ padding: "10px 12px", fontSize: 13, color: T.text, borderBottom: "1px solid " + T.border }}>
                  <div style={{ fontWeight: 600 }}>{u.display_name}</div>
                  <div style={{ fontSize: 11, color: T.textMuted, fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif" }}>{u.username}</div>
                </td>
                <td style={{ padding: "10px 12px", fontSize: 13, color: T.textSub, borderBottom: "1px solid " + T.border }}>{u.partner_name || "—"}</td>
                <td style={{ padding: "10px 12px", fontSize: 11, borderBottom: "1px solid " + T.border }}>
                  <span style={{ background: u.is_admin ? "#dbeafe" : u.is_staff ? "#fef3c7" : "#f0fdf4", color: u.is_admin ? "#1d4ed8" : u.is_staff ? "#b45309" : GREEN, padding: "2px 8px", borderRadius: 6, fontWeight: 700 }}>
                    {u.is_admin ? "Admin" : u.is_staff ? "Staff" : "Partner"}
                  </span>
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid " + T.border }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input
                      type="password" placeholder="New password…"
                      value={resetPw[u.id]||""}
                      onChange={e => setResetPw(p => ({...p,[u.id]:e.target.value}))}
                      style={{ ...iS, width: 150 }}
                    />
                    <button onClick={() => doResetPw(u.id)} style={{ background: T.bg, border: "1px solid " + T.border, borderRadius: 6, padding: "4px 10px", color: T.textSub, fontSize: 12, cursor: "pointer" }}>Set</button>
                  </div>
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid " + T.border }}>
                  {!u.is_admin && (
                    <button onClick={() => deleteUser(u.id, u.display_name)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── ACTIVITY PANEL ───────────────────────────────────────────────
function ActivityPanel() {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(API + "/api/activity", { headers: authHeaders() })
      .then(r => r.json())
      .then(data => { setLogs(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const fmtTime = (ts) => new Date(ts).toLocaleString("en-US", {
    timeZone: "America/New_York", month: "numeric", day: "numeric",
    year: "numeric", hour: "numeric", minute: "2-digit", hour12: true
  }) + " EST";

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.textMuted }}>Loading activity…</div>;

  return (
    <div>
      <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 16 }}>
        Last 100 partner login and inventory view events.
      </div>
      {logs.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: T.textMuted, fontSize: 14 }}>No activity recorded yet</div>
      ) : (
        <div style={{ background: T.surface, border: "1px solid " + T.border, borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: T.bg }}>
                {["Time", "Partner", "User", "Action"].map(h => (
                  <th key={h} style={{ padding: "10px 14px", fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "left", borderBottom: "2px solid " + T.border }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={l.id} style={{ background: i % 2 === 0 ? "transparent" : T.bg }}>
                  <td style={{ padding: "9px 14px", fontSize: 12, color: T.textMuted, borderBottom: "1px solid " + T.border, whiteSpace: "nowrap", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif" }}>{fmtTime(l.logged_at)}</td>
                  <td style={{ padding: "9px 14px", fontSize: 13, fontWeight: 600, color: T.text, borderBottom: "1px solid " + T.border }}>{l.partner_name || "—"}</td>
                  <td style={{ padding: "9px 14px", fontSize: 12, color: T.textSub, borderBottom: "1px solid " + T.border, fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif" }}>{l.username}</td>
                  <td style={{ padding: "9px 14px", fontSize: 12, borderBottom: "1px solid " + T.border }}>
                    <span style={{ background: l.action === "login" ? "#dbeafe" : "#f0fdf4", color: l.action === "login" ? "#1d4ed8" : GREEN, padding: "2px 10px", borderRadius: 6, fontWeight: 600, fontSize: 11 }}>
                      {l.action === "login" ? <><Icon name="lock" size={12} style={{ verticalAlign:"-0.1em", marginRight:4 }} />Login</> : <><Icon name="eye" size={12} style={{ verticalAlign:"-0.1em", marginRight:4 }} />Viewed inventory</>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: "8px 14px", borderTop: "1px solid " + T.border, fontSize: 11, color: T.textMuted }}>
            {logs.length} event{logs.length !== 1 ? "s" : ""}
          </div>
        </div>
      )}
    </div>
  );
}

// ── ADMIN DASHBOARD ───────────────────────────────────────────────
function AdminDashboard({ isStaff }) {
  const [tab,     setTab]     = useState(isStaff ? "preview" : "upload");
  const [summary, setSummary] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    fetch(API + "/api/summary", { headers: authHeaders() })
      .then(r => r.json()).then(setSummary).catch(() => {});
  }, [tab]);

  const tabs = [
    ...(!isStaff ? [{ id: "upload",   label: "Upload Inventory" }] : []),
    ...(!isStaff ? [{ id: "partners", label: "Partners" }] : []),
    ...(!isStaff ? [{ id: "users",    label: "Users" }] : []),
    { id: "preview",  label: "Preview Partner" },
    { id: "activity", label: "Partner Activity" },
  ];

  return (
    <div>
      {/* Summary bar */}
      {summary && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 24 }}>
          {[
            { label: "Partners",       val: summary.partners?.length || 0 },
            { label: "Inventory Lines",val: (summary.totalRows||0).toLocaleString() },
            { label: "Last Upload",    val: summary.lastUpload ? new Date(summary.lastUpload.uploaded_at.replace(" ", "T") + "Z").toLocaleString("en-US", { timeZone: "America/New_York", month: "numeric", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) + " EST" : "Never" },
          ].map(c => (
            <div key={c.label} style={{ background: T.surface, border: "1px solid " + T.border, borderRadius: 10, padding: "12px 16px" }}>
              <div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, textAlign: "center" }}>{c.label}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: T.text, textAlign: "center" }}>{c.val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid " + T.border, marginBottom: 20, gap: 0 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: "none", border: "none", borderBottom: tab === t.id ? "2px solid " + T.accent : "2px solid transparent",
            color: tab === t.id ? T.text : T.textMuted, padding: "8px 16px", cursor: "pointer",
            fontSize: 13, fontWeight: tab === t.id ? 700 : 400, fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif",
          }}>{t.label}</button>
        ))}
      </div>

      {tab === "upload"   && <UploadPanel />}
      {tab === "partners" && <PartnersPanel />}
      {tab === "users"    && <UsersPanel />}
      {tab === "activity" && <ActivityPanel />}
      {tab === "preview"  && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: T.textMuted, marginRight: 8 }}>Preview as partner:</label>
            <select
              value={preview||""}
              onChange={e => setPreview(e.target.value||null)}
              style={{ background: T.surface, border: "1px solid " + T.border, borderRadius: 6, padding: "6px 10px", fontSize: 13, color: T.text, outline: "none" }}
            >
              <option value="">— Select partner —</option>
              {(summary?.partners||[]).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {preview && <InventoryView previewPartnerId={preview} />}
        </div>
      )}
    </div>
  );
}

// ── CHANGE PASSWORD MODAL ─────────────────────────────────────────
function ChangePwModal({ onClose }) {
  const [cur, setCur] = useState(""); const [nxt, setNxt] = useState(""); const [err, setErr] = useState("");
  const submit = async () => {
    if (nxt.length < 8) return setErr("Min 8 characters");
    const r = await fetch(API + "/api/change-password", { method: "POST", headers: authHeaders(), body: JSON.stringify({ currentPassword: cur, newPassword: nxt }) });
    const d = await r.json();
    if (!r.ok) return setErr(d.error);
    onClose();
  };
  const iS = { width: "100%", background: T.inputBg, border: "1px solid " + T.border, borderRadius: 8, padding: "9px 12px", color: T.text, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif" };
  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000066", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500 }}>
      <div style={{ background: T.surface, border: "1px solid " + T.border, borderRadius: 14, padding: 24, width: "100%", maxWidth: 340 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 16 }}>Change Password</div>
        {err && <div style={{ background: "#fef2f2", borderRadius: 6, padding: "8px 10px", fontSize: 12, color: RED, marginBottom: 12 }}>{err}</div>}
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 11, color: T.textMuted, display: "block", marginBottom: 4 }}>Current Password</label><input type="password" style={iS} value={cur} onChange={e => setCur(e.target.value)} /></div>
        <div style={{ marginBottom: 20 }}><label style={{ fontSize: 11, color: T.textMuted, display: "block", marginBottom: 4 }}>New Password</label><input type="password" style={iS} value={nxt} onChange={e => setNxt(e.target.value)} onKeyDown={e => e.key==="Enter"&&submit()} /></div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={submit} style={{ flex: 1, background: T.accent, border: "none", borderRadius: 8, padding: 10, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Update</button>
          <button onClick={onClose} style={{ background: "none", border: "1px solid " + T.border, borderRadius: 8, padding: 10, color: T.textMuted, fontSize: 13, cursor: "pointer" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────
export default function App() {
  const [user,       setUser]       = useState(getToken() ? { displayName: localStorage.getItem("cp_user"), isAdmin: localStorage.getItem("cp_admin") === "1", isStaff: localStorage.getItem("cp_staff") === "1" } : null);
  const themeName = useThemeName();
  const [showChangePw, setShowChangePw] = useState(false);

  const handleLogin = (d) => setUser({ displayName: d.displayName, isAdmin: d.isAdmin, isStaff: d.isStaff });

  const handleLogout = async () => {
    try { await fetch(API + "/api/logout", { method: "POST", headers: authHeaders() }); } catch {}
    clearToken(); setUser(null);
  };

  if (!user) return <LoginScreen onLogin={handleLogin} />;

  return (
    <div style={{ fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", background: T.bg, minHeight: "100vh", color: T.text }}>
      {showChangePw && <ChangePwModal onClose={() => setShowChangePw(false)} />}

      {/* Header */}
      <div style={{ background: T.headerBg, borderBottom: "1px solid " + T.border, padding: "12px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, background: T.accent, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 900, color: "#fff" }}>D</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.text, lineHeight: 1.2 }}>Dewey's Bakery</div>
            <div style={{ fontSize: 11, color: T.textMuted }}>Co-Manufacturing Partner Portal</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{user.displayName}</div>
            <div style={{ fontSize: 11, color: T.textMuted }}>{user.isAdmin ? "Dewey's Admin" : user.isStaff ? "Dewey's Staff" : "Partner"}</div>
          </div>
          <button onClick={() => setShowChangePw(true)} style={{ background: T.bg, border: "1px solid " + T.border, borderRadius: 7, padding: "5px 10px", color: T.textSub, fontSize: 11, cursor: "pointer" }}>Password</button>
          <div style={{ display:"flex", border:"1px solid "+T.border, borderRadius:7, overflow:"hidden", marginRight:8 }}>
            {[{ k:"mono", ic:"contrast" }, { k:"light", ic:"sun" }, { k:"dark", ic:"moon" }].map(o => (
              <button key={o.k} onClick={() => setThemeName(o.k)} title={o.k.charAt(0).toUpperCase()+o.k.slice(1)+" theme"}
                style={{ background: themeName===o.k ? T.accent : "transparent", color: themeName===o.k ? "#fff" : T.textMuted, border:"none", padding:"5px 8px", cursor:"pointer", display:"flex", alignItems:"center" }}>
                <Icon name={o.ic} size={15} />
              </button>
            ))}
          </div>
          <button onClick={handleLogout} style={{ background: "none", border: "1px solid " + T.border, borderRadius: 7, padding: "5px 10px", color: T.textMuted, fontSize: 11, cursor: "pointer" }}>Sign Out</button>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 20px" }}>
        {(user.isAdmin || user.isStaff) ? (
          <AdminDashboard isStaff={user.isStaff} />
        ) : (
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.text, marginBottom: 4 }}>Your Inventory</div>
            <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 24 }}>Real-time view of your Dewey's Bakery co-manufacturing inventory</div>
            <InventoryView />
          </div>
        )}
      </div>
    </div>
  );
}
